import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import Checkbox from '@mui/material/Checkbox';
import { Box, CircularProgress, TextField, InputAdornment } from '@mui/material';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import { getBorder } from 'utils/Styles';
import { useTranslation } from 'react-i18next';

const not = (a, b) => {
    const bIds = b.map((item) => item.id);
    return a.filter((item) => !bIds.includes(item.id));
};

const intersection = (a, b) => {
    const bIds = b.map((item) => item.id);
    return a.filter((item) => bIds.includes(item.id));
};

const union = (a, b) => {
    const aIds = a.map((item) => item.id);
    const newItems = b.filter((item) => !aIds.includes(item.id));
    return [...a, ...newItems];
};

const sortByName = (items) => {
    return [...items].sort((a, b) => {
        const nameA = String(a?.name || '').toLowerCase();
        const nameB = String(b?.name || '').toLowerCase();
        return nameA.localeCompare(nameB, 'es', { sensitivity: 'base' });
    });
};

const ListTransfer = ({ loading, allItems, right, setRight, textLeft, textRight, textSelected }) => {
    const theme = useTheme();
    const [checked, setChecked] = useState([]);
    const [left, setLeft] = useState([]);
    const [searchLeft, setSearchLeft] = useState('');
    const [searchRight, setSearchRight] = useState('');
    const { t } = useTranslation();

    const leftChecked = intersection(checked, left);
    const rightChecked = intersection(checked, right);

    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }

        setChecked(newChecked);
    };

    const numberOfChecked = (items) => intersection(checked, items).length;

    const handleToggleAll = (items) => () => {
        if (numberOfChecked(items) === items.length) {
            setChecked(not(checked, items));
        } else {
            setChecked(union(checked, items));
        }
    };

    const handleCheckedRight = () => {
        setRight([...right, ...leftChecked]);
        setLeft(not(left, leftChecked));
        setChecked(not(checked, leftChecked));
    };

    const handleCheckedLeft = () => {
        setLeft([...left, ...rightChecked]);
        setRight(not(right, rightChecked));
        setChecked(not(checked, rightChecked));
    };

    const customList = (
        title,
        items,
        searchValue,
        setSearchValue,
        placeholderLeft = 'Buscar ...',
        placeholderRight = 'Buscar ...',
    ) => {
        const sortedItems = sortByName(items);
        const filteredItems = sortedItems.filter((item) =>
            String(item?.name || '')
                .toLowerCase()
                .includes(searchValue.toLowerCase()),
        );

        return (
            <Card
                sx={{
                    boxShadow: 'none',
                    border: (theme) => getBorder(theme.palette.mode),
                }}
            >
                <CardHeader
                    sx={{
                        px: 2,
                        py: 1,
                        backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#424242',
                        border: (theme) => getBorder(theme.palette.mode, 'bottom'),
                        borderBottom: '1px solid',
                        borderColor: theme.palette.mode === 'light' ? '#e0e0e0' : '#616161',
                    }}
                    avatar={
                        <Checkbox
                            onClick={handleToggleAll(items)}
                            checked={numberOfChecked(items) === items.length && items.length !== 0}
                            indeterminate={numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0}
                            disabled={loading || items.length === 0}
                        />
                    }
                    title={title}
                    subheader={`${numberOfChecked(items)}/${items.length} ${textSelected}`}
                />

                <Divider />

                <Box sx={{ p: 2 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder={items === left ? placeholderLeft : placeholderRight}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Box>

                <Divider />

                <List
                    sx={{
                        width: '100%',
                        maxWidth: '100%',
                        height: 230,
                        bgcolor: 'background.paper',
                        overflow: 'auto',
                    }}
                    dense
                    component="div"
                    role="list"
                >
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        filteredItems.map((value) => {
                            const labelId = `transfer-list-all-item-${value.id}-label`;

                            return (
                                <ListItemButton key={value.id} role="listitem" onClick={handleToggle(value)}>
                                    <ListItemIcon>
                                        <Checkbox
                                            checked={checked.some((i) => i.id === value.id)}
                                            tabIndex={-1}
                                            disableRipple
                                            slotProps={{
                                                input: {
                                                    'aria-labelledby': labelId,
                                                },
                                            }}
                                        />
                                    </ListItemIcon>

                                    <ListItemText id={labelId} primary={value.name || ''} />
                                </ListItemButton>
                            );
                        })
                    )}
                </List>
            </Card>
        );
    };

    useEffect(() => {
        const rightIds = right.map((item) => item.id);
        setLeft(
            allItems.filter((item) => !rightIds.includes(item.id)).map((item) => ({ id: item.id, name: item.name })),
        );
    }, [allItems, right]);

    return (
        <Grid container spacing={2} justifyContent="center" alignItems="center">
            <Grid size={{ xs: 5, sm: 5, md: 5, lg: 5, xl: 5 }}>
                {customList(
                    textLeft,
                    left,
                    searchLeft,
                    setSearchLeft,
                    t('general.transfer_list.search_available'),
                    t('general.transfer_list.search_selected'),
                )}
            </Grid>

            <Grid size={{ xs: 2, sm: 2, md: 2, lg: 2, xl: 2 }}>
                <Grid container direction="column" alignItems="center">
                    <Button
                        sx={{ my: 0.5 }}
                        variant="contained"
                        size="small"
                        onClick={handleCheckedRight}
                        disabled={leftChecked.length === 0}
                        aria-label="move selected right"
                    >
                        &gt;
                    </Button>

                    <Button
                        sx={{ my: 0.5 }}
                        variant="contained"
                        size="small"
                        onClick={handleCheckedLeft}
                        disabled={rightChecked.length === 0}
                        aria-label="move selected left"
                    >
                        &lt;
                    </Button>
                </Grid>
            </Grid>

            <Grid size={{ xs: 5, sm: 5, md: 5, lg: 5, xl: 5 }}>
                {customList(
                    textRight,
                    right,
                    searchRight,
                    setSearchRight,
                    t('general.transfer_list.search_available'),
                    t('general.transfer_list.search_selected'),
                )}
            </Grid>
        </Grid>
    );
};

export default ListTransfer;
